import { PlacementYAnchor, TextAlignment, TextVerticalAlignment } from "building-types/annotation/annotationTypesEnum";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import { sheetPolylineGeometryNew, sheetTextGeometryNew } from "building-types/views/sheetGeometryTypes";
import { DimensionPrecision } from "building-types/views/viewTypes";
import { ZERO_INCHES } from "common/constants/scaleFactors";
import type { SheetInches } from "common/types/nominalNumbersTypes";
import { degreeToRadian, radianToDegree } from "common/utils/math";
import { sheetPointsNew, sheetPointsToInches } from "common/utils/nominalNumbers";
import { getOrientedDimLine } from "doc/dimensions";
import { DIM_LW, DIM_TICK_LW } from "doc/plans/constants";
import { normalizeTextRotation } from "doc/plans/primitives/textPrimitive";
import { line2Vec } from "geom/line2";
import { mat3Dot, mat3Mult, mat3RotateAroundPoint, mat3Translate } from "geom/mat3";
import { polyLine2ByTwoPoints, polyLine2EdgeEval } from "geom/polyLine2";
import { vec2Add, vec2AngleBetween, vec2Copy, vec2Negate, vec2Normalize, vec2Rotate, vec2Scale } from "geom/vec2";
import colors from "ui/styles/colors";
import { formatDimText } from "./formatDimText";

interface DimTagProps {
  x?: SheetInches;
  y1: SheetInches;
  x1: SheetInches;
  x2: SheetInches;
  y2: SheetInches;
  prefix?: string;
  dimVal: number;
  suffix?: string;
  offset?: SheetInches;

  dimensionPrecision?: DimensionPrecision;
}

export const dimTagDefault: Required<DimTagProps> = {
  x: ZERO_INCHES,
  x1: ZERO_INCHES,
  y1: ZERO_INCHES,
  x2: sheetPointsToInches(sheetPointsNew(30)),
  y2: sheetPointsToInches(sheetPointsNew(30)),
  prefix: "",
  dimVal: 100,
  suffix: "",
  offset: ZERO_INCHES,
  dimensionPrecision: DimensionPrecision.PRECISION_1_8,
};

function calculateTagGeometries(props: DimTagProps) {
  const { x1, y1, x2, y2 } = props;

  const lineVec = line2Vec([x1, y1, x2, y2]);
  const [normX, normY] = vec2Normalize(lineVec[0], lineVec[1]);

  const extendVec = vec2Copy(vec2Scale(normX, normY, 0.05));
  const [extendPosX, extendPosY] = extendVec;
  const [extendNegX, extendNegY] = vec2Negate(extendVec[0], extendVec[1]);
  const start = vec2Copy(vec2Add(x1, y1, extendNegX, extendNegY));
  const end = vec2Copy(vec2Add(x2, y2, extendPosX, extendPosY));

  return {
    x1,
    y1,
    x2,
    y2,
    normX,
    normY,
    extendPosX,
    extendPosY,
    start,
    end,
  };
}

export function generateEdgeDimTag(propsPartial: DimTagProps) {
  const props = { ...dimTagDefault, ...propsPartial };
  const result: SheetGeometry[] = [];

  const {
    text,
    x,
    y,
    x1,
    y1,
    x2,
    y2,
    start,
    end,
    rotateAngle,
    extensionOffsetVecX,
    extensionOffsetVecY,
    dimAngleRads,
  } = calculateEdgeDimTag(props);
  const hashLineLength = 0.1;
  const x1Ext = start[0];
  const y1Ext = start[1];
  const x2Ext = end[0];
  const y2Ext = end[1];

  const transform = mat3Translate(extensionOffsetVecX + (props.x ?? 0), extensionOffsetVecY);

  const [s1tx, s1ty] = mat3Dot(transform, x1, y1);
  const s1ExtensionOffsetT = vec2Copy(mat3Dot(transform, x1 - extensionOffsetVecX, y1 - extensionOffsetVecY));
  result.push(
    sheetPolylineGeometryNew({
      paths: [polyLine2ByTwoPoints(s1ExtensionOffsetT[0], s1ExtensionOffsetT[1], s1tx, s1ty)],
      linewidth: DIM_LW,
    }),
  );
  const [s2tx, s2ty] = mat3Dot(transform, x2, y2);
  const [s2ExtensionOffsetTx, s2ExtensionOffsetTy] = mat3Dot(
    transform,
    x2 - extensionOffsetVecX,
    y2 - extensionOffsetVecY,
  );
  result.push(
    sheetPolylineGeometryNew({
      paths: [polyLine2ByTwoPoints(s2ExtensionOffsetTx, s2ExtensionOffsetTy, s2tx, s2ty)],
      linewidth: DIM_LW,
    }),
  );
  const [ext1tx, ext1ty] = mat3Dot(transform, x1Ext, y1Ext);
  const [ext2tx, ext2ty] = mat3Dot(transform, x2Ext, y2Ext);
  result.push(
    sheetPolylineGeometryNew({
      paths: [polyLine2ByTwoPoints(ext1tx, ext1ty, ext2tx, ext2ty)],
      linewidth: DIM_LW,
    }),
  );

  const rotateAngleRad = degreeToRadian(rotateAngle);
  const transformRotS1 = mat3Mult(transform, mat3RotateAroundPoint(x1, y1, rotateAngleRad));
  const [sHashline1x, sHashline1y] = mat3Dot(transformRotS1, x1 - hashLineLength / 2, y1);
  const [eHashline1x, eHashline1y] = mat3Dot(transformRotS1, x1 + hashLineLength / 2, y1);

  result.push(
    sheetPolylineGeometryNew({
      paths: [polyLine2ByTwoPoints(sHashline1x, sHashline1y, eHashline1x, eHashline1y)],
      linewidth: DIM_TICK_LW,
    }),
  );

  const transformRotS2 = mat3Mult(transform, mat3RotateAroundPoint(x2, y2, rotateAngleRad));
  const [sHashline2x, sHashline2y] = mat3Dot(transformRotS2, x2 - hashLineLength / 2, y2);
  const [eHashline2x, eHashline2y] = mat3Dot(transformRotS2, x2 + hashLineLength / 2, y2);

  result.push(
    sheetPolylineGeometryNew({
      paths: [polyLine2ByTwoPoints(sHashline2x, sHashline2y, eHashline2x, eHashline2y)],
      linewidth: DIM_TICK_LW,
    }),
  );

  const [labelTx, labelTy] = mat3Dot(transform, x, y);
  result.push(
    sheetTextGeometryNew({
      x: labelTx,
      y: labelTy,
      text: text,
      rotation: dimAngleRads,
      alignment: TextAlignment.CENTER,
      verticalAlignment: TextVerticalAlignment.BOTTOM,
      placementYAnchor: PlacementYAnchor.BOTTOM,
      strokeColor: colors.white,
      strokeWidth: sheetPointsNew(2),
    }),
  );
  return result;
}

function calculateEdgeDimTag(props: DimTagProps) {
  const { x1, y1, x2, y2, normX, normY, start, end } = calculateTagGeometries(props);

  const text = formatDimText(props);

  const dimLine = getOrientedDimLine(
    y1 < y2 ? polyLine2ByTwoPoints(x1, y1, x2, y2) : polyLine2ByTwoPoints(x2, y2, x1, y1),
  );
  const [x, y] = polyLine2EdgeEval(dimLine, 0, 0.5, true);
  const dimAngleRadsNotNormalized = -vec2AngleBetween(1, 0, dimLine[2] - dimLine[0], dimLine[3] - dimLine[1]);
  const { flipped } = normalizeTextRotation(dimAngleRadsNotNormalized, 0);
  const dimAngleRads = flipped ? dimAngleRadsNotNormalized + Math.PI : dimAngleRadsNotNormalized;

  const angleRad = vec2AngleBetween(0, 1, normX, normY);
  const rotateAngle = radianToDegree(angleRad) + 45;

  const dimOffset = props.offset ?? 0;
  const [extensionX, extensionY] = vec2Scale(normX, normY, dimOffset);
  const [extensionOffsetVecX, extensionOffsetVecY] = vec2Rotate(extensionX, extensionY, degreeToRadian(270));

  return {
    text,
    x,
    y,
    x1,
    y1,
    x2,
    y2,
    start,
    end,
    rotateAngle,
    extensionOffsetVecX,
    extensionOffsetVecY,
    dimAngleRads,
  };
}
